import { forwardRef, memo, useMemo, useRef, type FocusEvent, type KeyboardEvent, type SyntheticEvent } from 'react';
import en from 'date-fns/locale/en-GB';
import type { ReactDatePickerProps } from 'react-datepicker';
import ReactDatePicker, { registerLocale } from 'react-datepicker';
import { useTheme } from 'styled-components';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef/index.ts';

import Icons from '../../../Elements/Icons/index.ts';
import { DatePickerInput } from '../components/DatePickerInput.tsx';
import { YearSelect, MonthSelect, TimePresets } from '../components/index.ts';
import {
  DATE_FORMAT,
  KEY_DATE_FORMAT,
  KEY_ENTER,
  CALENDAR_CLASSNAME,
  CALENDAR_PORTAL_ID,
  DATE_PICKER_REF,
  DEFAULT_TIME_INTERVALS,
  TIME_FORMAT,
  KEY_TIME_FORMAT,
  DATE_TIME_PLACEHOLDER,
  DATE_PLACEHOLDER,
  DATE_PICKER_INPUT_MIN_WIDTH,
  DEFAULT_DATE_PICKER_TRANSLATIONS,
} from '../constants.ts';
import { convertDateToTimezone, generateTimePresets, getValidDate, toLocalDisplayDate } from '../helpers.ts';
import { usePreventCalendarCloseOnTouch } from '../hooks/index.ts';
import { DatePickerStyles, DatePickerHeaderWrapper } from '../styled.ts';
import type { DatePickerTranslations, DateType } from '../types.ts';

export interface DatePickerProps extends ReactDatePickerProps {
  dataTestId?: string;
  showLocal?: boolean;
  dateFormatKey?: string;
  timeFormatKey?: string;
  error?: boolean;
  warning?: boolean;
  locale?: string;
  translations?: DatePickerTranslations;
}

export const DatePicker = memo(
  forwardRef<HTMLInputElement, DatePickerProps>(function DatePicker(
    {
      dataTestId,
      dateFormat = DATE_FORMAT,
      timeFormat = TIME_FORMAT,
      dateFormatKey = KEY_DATE_FORMAT,
      timeFormatKey = KEY_TIME_FORMAT,
      selected,
      showLocal,
      onChange,
      onBlur,
      onKeyDown,
      value,
      error,
      warning,
      locale = 'en',
      isClearable,
      minDate,
      maxDate,
      showTimeSelectOnly: _showTimeSelectOnly,
      showTimeSelect,
      timeIntervals = DEFAULT_TIME_INTERVALS,
      shouldCloseOnSelect: _shouldCloseOnSelect,
      translations,
      ...props
    },
    ref,
  ) {
    const { colors } = useTheme();
    const datePickerRef = useRef<ReactDatePicker>(null);
    registerLocale(locale, en);

    const { handleCalendarOpen, handleCalendarClose } = usePreventCalendarCloseOnTouch();

    const mergedTranslations = useMemo<Required<DatePickerTranslations>>(
      () => ({
        ...DEFAULT_DATE_PICKER_TRANSLATIONS,
        ...translations,
      }),
      [translations],
    );

    const formatKey = useMemo(
      () => (showTimeSelect ? `${dateFormatKey} ${timeFormatKey}` : dateFormatKey),
      [dateFormatKey, showTimeSelect, timeFormatKey],
    );

    const selectedDate = useMemo((): DateType | undefined => {
      if (!value) {
        return selected;
      }
      return getValidDate({ value, dateFormatKey: formatKey, showLocal, minDate, maxDate }) ?? selected;
    }, [value, formatKey, showLocal, minDate, maxDate, selected]);

    const displayDate = useMemo(() => (selectedDate ? toLocalDisplayDate(selectedDate, showLocal) : undefined), [selectedDate, showLocal]);

    const timePresets = useMemo(() => generateTimePresets(timeIntervals), [timeIntervals]);

    const handleKeyDown = useCallbackRef((e: KeyboardEvent<HTMLInputElement>) => {
      if (onKeyDown) {
        onKeyDown(e);
      } else if (e.key === KEY_ENTER && e.target instanceof HTMLInputElement) {
        onChange(getValidDate({ value: e.currentTarget.value, dateFormatKey: formatKey, showLocal, minDate, maxDate }), e);
      }
    });

    const handleBlur = useCallbackRef((e: FocusEvent<HTMLInputElement>) => {
      onChange(getValidDate({ value: e.target.value, dateFormatKey: formatKey, showLocal, minDate, maxDate }), e);
      onBlur?.(e);
    });

    const onClear = useCallbackRef(() => {
      onChange(null, undefined);
    });

    const handleTimeChange = useCallbackRef((date: Date | null, e: SyntheticEvent<unknown> | undefined) => {
      const convertedDate = date ? convertDateToTimezone(date, showLocal, true) : null;
      onChange(convertedDate, e);
      datePickerRef.current?.setOpen(false);
    });

    const handleDateChange = useCallbackRef((date: Date | null, event: SyntheticEvent<unknown> | undefined) => {
      const convertedDate = date ? convertDateToTimezone(date, showLocal) : null;
      onChange(convertedDate, event);
    });

    return (
      <>
        <DatePickerStyles />
        <ReactDatePicker
          popperClassName={CALENDAR_CLASSNAME}
          customInputRef={DATE_PICKER_REF}
          customInput={
            <DatePickerInput
              minWidth={showTimeSelect ? DATE_PICKER_INPUT_MIN_WIDTH : undefined}
              dataTestId={dataTestId}
              icon={
                isClearable && selected ? (
                  <Icons.Clear filled size={20} color={colors.grey70_1} onClick={onClear} dataTestId='icons-clear' />
                ) : (
                  <Icons.Calendar size={20} color={colors.grey100_1} />
                )
              }
              error={error}
              warning={warning}
              isActionIcon={isClearable && !!selected}
              ref={ref}
            />
          }
          renderCustomHeader={({ date, changeYear, changeMonth }) => (
            <DatePickerHeaderWrapper>
              <MonthSelect date={date} changeMonth={changeMonth} changeYear={changeYear} locale={locale} dataTestId={dataTestId} />
              <YearSelect
                date={date}
                changeYear={changeYear}
                minYear={minDate?.getFullYear()}
                maxYear={maxDate?.getFullYear()}
                dataTestId={dataTestId}
              />
            </DatePickerHeaderWrapper>
          )}
          value={value}
          onChange={handleDateChange}
          selected={displayDate}
          dateFormat={showTimeSelect ? `${dateFormat} ${timeFormat}` : dateFormat}
          timeFormat={timeFormat}
          locale={locale}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          placeholderText={showTimeSelect ? DATE_TIME_PLACEHOLDER : DATE_PLACEHOLDER}
          portalId={CALENDAR_PORTAL_ID}
          minDate={minDate}
          maxDate={maxDate}
          shouldCloseOnSelect={!showTimeSelect}
          onCalendarOpen={handleCalendarOpen}
          onCalendarClose={handleCalendarClose}
          ref={datePickerRef}
          {...props}>
          {showTimeSelect && (
            <TimePresets
              presets={timePresets}
              onChange={handleTimeChange}
              date={displayDate}
              isDateSelected={!!selectedDate}
              dataTestId={dataTestId}
              translations={mergedTranslations}
            />
          )}
        </ReactDatePicker>
      </>
    );
  }),
);
